import React, { useEffect, useRef } from 'react';
import { GameState, CardType } from '../types';
import * as gameLogic from '../services/gameLogic';
import { useParticles } from '../contexts/ParticleContext';
import { UI_ANCHOR_IDS, uiAnchorRegistry } from '../services/uiAnchorRegistry';

export const useGameEffects = (
    gameState: GameState | null,
    localPlayerId: number,
    playerAvatarRef: React.RefObject<HTMLDivElement>,
    aiAvatarRef: React.RefObject<HTMLDivElement>
) => {
    const lastChainVisualKeyRef = useRef<string | null>(null);
    const particleRef = useParticles();

    // DOM Caching for Towers
    const cachedTowerRects = useRef<Record<string, DOMRect>>({});
    const lastProcessedLogLength = useRef<number>(0);
    const refreshFrameRef = useRef<number | null>(null);
    const refreshSettleFrameRef = useRef<number | null>(null);

    const getLocalAvatarRect = () => uiAnchorRegistry.getRect(UI_ANCHOR_IDS.localAvatar) ?? playerAvatarRef.current?.getBoundingClientRect() ?? null;
    const getOpponentAvatarRect = () => uiAnchorRegistry.getRect(UI_ANCHOR_IDS.opponentAvatar) ?? aiAvatarRef.current?.getBoundingClientRect() ?? null;
    const getAvatarRectForPlayerId = (playerId?: number | null) => (playerId === localPlayerId ? getLocalAvatarRect() : getOpponentAvatarRect());
    const getBoardRect = () => uiAnchorRegistry.getRect(UI_ANCHOR_IDS.gameBoard) ?? uiAnchorRegistry.getRect(UI_ANCHOR_IDS.shellFrame) ?? null;
    const getHandRailRect = () => uiAnchorRegistry.getRect(UI_ANCHOR_IDS.handRail) ?? null;
    const getPrepZoneRect = () => uiAnchorRegistry.getRect(UI_ANCHOR_IDS.prepZone) ?? null;
    const getRectCenter = (rect: DOMRect | null) => (rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : null);
    const getViewportCenter = () => {
        const boardRect = getBoardRect();
        if (boardRect) {
            return getRectCenter(boardRect)!;
        }

        return {
            x: window.innerWidth / 2,
            y: window.innerHeight / 2,
        };
    };
    const getLocalHandPoint = () => {
        const handRect = getHandRailRect();
        const boardRect = getBoardRect();
        if (handRect) return getRectCenter(handRect)!;
        if (boardRect) {
            return {
                x: boardRect.left + boardRect.width / 2,
                y: boardRect.bottom - Math.min(100, boardRect.height * 0.12),
            };
        }
        return { x: window.innerWidth / 2, y: window.innerHeight - 100 };
    };
    const getOpponentHandPoint = () => {
        const boardRect = getBoardRect();
        if (boardRect) {
            return {
                x: boardRect.left + boardRect.width / 2,
                y: boardRect.top + Math.min(100, boardRect.height * 0.12),
            };
        }
        return { x: window.innerWidth / 2, y: 100 };
    };
    const getLocalPrepPoint = () => {
        const prepRect = getPrepZoneRect();
        const boardRect = getBoardRect();
        if (prepRect) return getRectCenter(prepRect)!;
        if (boardRect) {
            return {
                x: boardRect.left + boardRect.width / 2,
                y: boardRect.bottom - Math.min(320, boardRect.height * 0.28),
            };
        }
        return { x: window.innerWidth / 2, y: window.innerHeight - 320 };
    };
    const getOpponentPrepPoint = () => {
        const boardRect = getBoardRect();
        if (boardRect) {
            return {
                x: boardRect.left + boardRect.width / 2,
                y: boardRect.top + Math.min(320, boardRect.height * 0.28),
            };
        }
        return { x: window.innerWidth / 2, y: 320 };
    };

    const refreshTowerRects = () => {
        const towerElements = document.querySelectorAll('[data-tower-color]');
        towerElements.forEach(el => {
            const color = el.getAttribute('data-tower-color');
            const pId = el.getAttribute('data-player-id');
            if (color && pId) {
                cachedTowerRects.current[`${pId}-${color}`] = el.getBoundingClientRect();
            }
        });

        const emptyTowerElements = document.querySelectorAll('[data-empty-tower-slot]');
        emptyTowerElements.forEach(el => {
            const id = el.getAttribute('id');
            if (id) {
                cachedTowerRects.current[id] = el.getBoundingClientRect();
            }
        });
    };

    const scheduleTowerRectRefresh = () => {
        if (refreshFrameRef.current !== null || refreshSettleFrameRef.current !== null) {
            return;
        }

        refreshFrameRef.current = requestAnimationFrame(() => {
            refreshFrameRef.current = null;
            refreshSettleFrameRef.current = requestAnimationFrame(() => {
                refreshSettleFrameRef.current = null;
                refreshTowerRects();
            });
        });
    };

    useEffect(() => {
        const handleViewportChange = () => {
            scheduleTowerRectRefresh();
        };

        scheduleTowerRectRefresh();
        window.addEventListener('resize', handleViewportChange);
        window.addEventListener('scroll', handleViewportChange, true);

        return () => {
            window.removeEventListener('resize', handleViewportChange);
            window.removeEventListener('scroll', handleViewportChange, true);
            if (refreshFrameRef.current !== null) {
                cancelAnimationFrame(refreshFrameRef.current);
            }
            if (refreshSettleFrameRef.current !== null) {
                cancelAnimationFrame(refreshSettleFrameRef.current);
            }
        };
    }, []);

    useEffect(() => {
        scheduleTowerRectRefresh();
    }, [gameState?.phase, gameState?.players, gameState?.activations?.length, gameState?.log?.length]);

    // Watch for specific game events (like Tower Steals) that don't always fire via activations
    useEffect(() => {
        if (!gameState || !particleRef) return;

        const currentLogLength = gameState.log.length;

        if (currentLogLength < lastProcessedLogLength.current) {
            lastProcessedLogLength.current = 0;
        }

        if (currentLogLength > lastProcessedLogLength.current) {
            const newLogs = gameState.log.slice(lastProcessedLogLength.current);

            newLogs.forEach(logEntry => {
                // Handle Life Charger auto-trigger animation (day-time disease/naturalgrip death prevention)
                if (logEntry.startsWith('t:log.lifeChargerAuto|')) {
                    const matchPlayer = logEntry.match(/playerName=([^|]+)/);
                    if (matchPlayer) {
                        const playerName = matchPlayer[1];
                        const lcPlayer = gameState.players.find(p => p.name === playerName);
                        if (lcPlayer) {
                            const rect = getAvatarRectForPlayerId(lcPlayer.id);
                            if (rect) {
                                particleRef.spawnLifeCharger(rect.left + rect.width / 2, rect.top + rect.height / 2);
                            }
                        }
                    }
                }

                // Handle Astrid Blast
                // We check the [REWARD] tag to ensure it triggers exactly once per usage
                const astridRewardMatch = logEntry.match(/\[REWARD\] HERO_POWER_USED:PLAYER=(\d+):TYPE=ASTRID_BLAST/);
                
                if (astridRewardMatch) {
                    const attackerId = parseInt(astridRewardMatch[1], 10);
                    const isAttackerLocal = attackerId === localPlayerId;

                    const trySpawnAstridBow = (retriesLeft: number) => {
                        requestAnimationFrame(() => {
                            const attackerRect = isAttackerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
                            const defenderRect = isAttackerLocal ? getOpponentAvatarRect() : getLocalAvatarRect();

                            if (attackerRect && defenderRect) {
                                const sx = attackerRect.left + attackerRect.width / 2;
                                const sy = attackerRect.top + attackerRect.height / 2;
                                const tx = defenderRect.left + defenderRect.width / 2;
                                const ty = defenderRect.top + defenderRect.height / 2;
                                particleRef.spawnAstridBow(sx, sy, tx, ty);
                            } else if (retriesLeft > 0) {
                                trySpawnAstridBow(retriesLeft - 1);
                            } else {
                                const center = getViewportCenter();
                                const offset = Math.min(center.y * 0.4, window.innerHeight * 0.25, 200);
                                particleRef.spawnAstridBow(center.x, center.y + offset, center.x, center.y - offset);
                            }
                        });
                    };
                    trySpawnAstridBow(6);
                }

                // Handle StealTower (Purification)
                if (logEntry.startsWith('t:log.towerStole|') || logEntry.startsWith('t:log.towerClaimed|')) {
                    const matchPlayer = logEntry.match(/playerName=([^|]+)/);
                    const matchColor = logEntry.match(/targetColor=([^|]+)/);
                    const matchDefender = logEntry.match(/defenderName=([^|]+)/);
                    const matchMethod = logEntry.match(/method=([^|]+)/);

                    if (matchPlayer && matchColor) {
                        const attackerName = matchPlayer[1];
                        const targetColor = matchColor[1];
                        const isSteal = !!matchDefender;
                        const isPurification = matchMethod && matchMethod[1] === 'purification';

                        const attacker = gameState.players.find(p => p.name === attackerName);
                        if (!attacker) return;
                        const defender = gameState.players.find(p => p.id !== attacker.id);
                        if (!defender) return;

                        if (isPurification) {
                            const isAttackerLocal = attacker.id === localPlayerId;
                            const viewportCenter = getViewportCenter();

                            let startX = viewportCenter.x;
                            let startY = viewportCenter.y;

                            if (isSteal) {
                                const cachedRect = cachedTowerRects.current[`${defender.id}-${targetColor}`];
                                if (cachedRect) {
                                    startX = cachedRect.left + cachedRect.width / 2;
                                    startY = cachedRect.top + cachedRect.height / 2;
                                } else {
                                    const defRect = isAttackerLocal ? getOpponentAvatarRect() : getLocalAvatarRect();
                                    if (defRect) {
                                        startX = defRect.left + defRect.width / 2;
                                        startY = defRect.top + defRect.height / 2;
                                    }
                                }
                            } else {
                                const defRect = isAttackerLocal ? getOpponentAvatarRect() : getLocalAvatarRect();
                                if (defRect) {
                                    startX = defRect.left + defRect.width / 2;
                                    startY = defRect.top + defRect.height / 2;
                                }
                            }

                            const trySpawnPurification = (retriesLeft: number) => {
                                requestAnimationFrame(() => {
                                    let endX = viewportCenter.x;
                                    let endY = viewportCenter.y;

                                    const newRect = document.getElementById(`tower-${attacker.id}-${targetColor}`)?.getBoundingClientRect();
                                    if (newRect) {
                                        endX = newRect.left + newRect.width / 2;
                                        endY = newRect.top + newRect.height / 2;
                                    } else if (retriesLeft > 0) {
                                        trySpawnPurification(retriesLeft - 1);
                                        return;
                                    } else {
                                        const attRect = isAttackerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
                                        if (attRect) {
                                            endX = attRect.left + attRect.width / 2;
                                            endY = attRect.top + attRect.height / 2;
                                        }
                                    }

                                    particleRef.spawnPurification(startX, startY, endX, endY);
                                });
                            };
                            trySpawnPurification(6);
                        }
                    }
                }
            });
            lastProcessedLogLength.current = currentLogLength;
        }
    }, [gameState?.log, gameState?.log?.length, particleRef]);

    useEffect(() => {
        if (!gameState || !particleRef) return;
        if (!gameState.players || !gameState.activations) return;

        // Derive current activation and player
        const currentActivation = gameState.activations.length > 0 ? gameState.activations[gameState.activations.length - 1] : null;
        const player = gameState.players.find(p => p.id === localPlayerId);

        const act = currentActivation;
        if (!act) return;

        const latest = act.counterChain.length > 0 ? act.counterChain[act.counterChain.length - 1] : act.attackingCard;
        const eff = gameLogic.getCardEffect(latest);
        const key = `${act.attackingCard.card.id}:${act.counterChain.length}`;

        if (lastChainVisualKeyRef.current === key) return;
        lastChainVisualKeyRef.current = key;

        const viewportCenter = getViewportCenter();
        const centerX = viewportCenter.x;
        const centerY = viewportCenter.y;

        // Helper to safely get rects
        // If player is not found, we might default to null or handle it. 
        // In the original code, 'player' was expected to be defined.
        // We use optional chaining for safety.
        const targetRect = getAvatarRectForPlayerId(act.targetId);
        const ownerRect = getAvatarRectForPlayerId(latest.card.ownerId);

        const tx = targetRect ? (targetRect.left + targetRect.width / 2) : centerX;
        const ty = targetRect ? (targetRect.top + targetRect.height / 2) : centerY;
        // tw and th were unused in some paths but used in others
        // const tw = targetRect ? targetRect.width : 0;
        // const th = targetRect ? targetRect.height : 0;

        const ox = ownerRect ? (ownerRect.left + ownerRect.width / 2) : centerX;
        const oy = ownerRect ? (ownerRect.top + ownerRect.height / 2) : centerY;

        if (eff.type === CardType.Attack) {
            if ((latest.card as any).faceSlug === 'fireball' && targetRect) {
                particleRef.spawnFireball(ox, oy, tx, ty);
            } else if ((latest.card as any).faceSlug === 'paladins-hammer' && targetRect) {
                // isTargetPerspective = true when the local player is being hit by the hammer
                const isTargetPerspective = act.targetId === player?.id;
                particleRef.spawn3DHammerSlam(ox, oy, tx, ty, isTargetPerspective);
            } else if (latest.card.id === 'Sword of Revenge' || (latest.card as any).faceSlug === 'sword-of-revenge') {
                const isOwnerLocal = latest.card.ownerId === localPlayerId;
                const ownerRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
                const oppRect = isOwnerLocal ? getOpponentAvatarRect() : getLocalAvatarRect();

                const sourceX = ownerRect ? ownerRect.left + ownerRect.width / 2 : ox;
                const sourceY = ownerRect ? ownerRect.top + ownerRect.height / 2 : oy;
                const targetX = oppRect ? oppRect.left + oppRect.width / 2 : centerX;
                const targetY = oppRect ? oppRect.top + oppRect.height / 2 : centerY;

                particleRef.spawnSwordOfRevenge(sourceX, sourceY, targetX, targetY);
            } else if (latest.card.id === 'The Sting' || (latest.card as any).faceSlug === 'the-sting') {
                const isOwnerLocal = latest.card.ownerId === localPlayerId;
                const ownerRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
                const oppRect = isOwnerLocal ? getOpponentAvatarRect() : getLocalAvatarRect();

                const sourceX = ownerRect ? ownerRect.left + ownerRect.width / 2 : ox;
                const sourceY = ownerRect ? ownerRect.top + ownerRect.height / 2 : oy;
                const targetX = oppRect ? oppRect.left + oppRect.width / 2 : centerX;
                const targetY = oppRect ? oppRect.top + oppRect.height / 2 : centerY;

                particleRef.spawnTheSting(sourceX, sourceY, targetX, targetY, !isOwnerLocal);
            } else if (latest.card.id === 'Ancient Sword' || (latest.card as any).faceSlug === 'ancient-sword') {
                const isOwnerLocal = latest.card.ownerId === localPlayerId;
                const ownerRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
                const oppRect = isOwnerLocal ? getOpponentAvatarRect() : getLocalAvatarRect();

                const sourceX = ownerRect ? ownerRect.left + ownerRect.width / 2 : ox;
                const sourceY = ownerRect ? ownerRect.top + ownerRect.height / 2 : oy;
                const targetX = oppRect ? oppRect.left + oppRect.width / 2 : centerX;
                const targetY = oppRect ? oppRect.top + oppRect.height / 2 : centerY;

                particleRef.spawnAncientSword(sourceX, sourceY, targetX, targetY);
            } else if (targetRect) {
                particleRef.spawnAttackEpic(targetRect.left, targetRect.top);
            }
        } else if (eff.type === CardType.Defense && (latest.card as any).faceSlug === 'ancient-shield') {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const ownerRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
            if (ownerRect) {
                const ox = ownerRect.left + ownerRect.width / 2;
                const oy = ownerRect.top + ownerRect.height / 2;
                particleRef.spawnAncientShield(ox, oy, !isOwnerLocal);
            }
        } else if (eff.type === CardType.Defense && (latest.card as any).faceSlug === 'steel-shield') {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const ownerRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
            if (ownerRect) {
                const ox = ownerRect.left + ownerRect.width / 2;
                const oy = ownerRect.top + ownerRect.height / 2;
                particleRef.spawnSteelShield(ox, oy, !isOwnerLocal);
            }
        } else if (eff.type === CardType.Defense && (latest.card as any).faceSlug === 'shimmering-shield') {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const ownerRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
            if (ownerRect) {
                const ox = ownerRect.left + ownerRect.width / 2;
                const oy = ownerRect.top + ownerRect.height / 2;
                particleRef.spawnShimmeringShield(ox, oy, !isOwnerLocal);
            }
        } else if (eff.type === CardType.Pushback) particleRef.spawnPushbackShield();
        else if (eff.type === CardType.Dispel) particleRef.spawnDispelShush();
        else if (eff.type === CardType.Hibernation && ownerRect) particleRef.spawnHibernationIce(ownerRect.left, ownerRect.top, ownerRect.width, ownerRect.height);
        else if (eff.type === CardType.Frost && targetRect) particleRef.spawnHibernationIce(targetRect.left, targetRect.top, targetRect.width, targetRect.height);
        else if (eff.type === CardType.Heal && targetRect) particleRef.spawnHeal(targetRect.left, targetRect.top, targetRect.width, targetRect.height);
        else if (eff.type === CardType.Ale) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const ownerRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
            if (ownerRect) {
                particleRef.spawnAle(ownerRect.left + ownerRect.width / 2, ownerRect.top + ownerRect.height / 2);
            }
        }
        else if (eff.type === CardType.BodyUpgrade) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            particleRef.spawnBodyUpgrade(ox, oy, !isOwnerLocal);
        }
        else if (eff.type === CardType.Disease && targetRect) {
            // Target the actual target of the card (can be self or opponent)
            particleRef.spawnDiseaseSlime(targetRect.left, targetRect.top, targetRect.width, targetRect.height);
        }
        else if (eff.type === CardType.Steal) {
            // Steal goes from Owner Hand -> Opponent Hand -> Owner Hand
            // Hand positions:
            // Player (Bottom): window.innerHeight - 100
            // Opponent (Top): 100

            const isOwnerLocal = latest.card.ownerId === localPlayerId;

            // If I play it: My Hand (Bottom) -> Opponent Hand (Top)
            // If Opponent plays it: Opponent Hand (Top) -> My Hand (Bottom)

            const localHandPoint = getLocalHandPoint();
            const opponentHandPoint = getOpponentHandPoint();

            const sX = isOwnerLocal ? localHandPoint.x : opponentHandPoint.x;
            const sY = isOwnerLocal ? localHandPoint.y : opponentHandPoint.y;

            const tX = isOwnerLocal ? opponentHandPoint.x : localHandPoint.x;
            const tY = isOwnerLocal ? opponentHandPoint.y : localHandPoint.y;

            if (latest.card.id === 'Shadow Burglary') {
                const tr = isOwnerLocal ? getOpponentAvatarRect() : getLocalAvatarRect();
                const or = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
                const sb_sx = or ? or.left + or.width / 2 : sX;
                const sb_sy = or ? or.top + or.height / 2 : sY;
                const sb_tx = tr ? tr.left + tr.width / 2 : tX;
                const sb_ty = tr ? tr.top + tr.height / 2 : tY;
                particleRef.spawnShadowBurglary(sb_sx, sb_sy, sb_tx, sb_ty);
            } else {
                particleRef.spawnStealGhostHand(sX, sY, tX, tY);
            }
        }
        else if (eff.type === CardType.DoubleCross) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const prepPoint = isOwnerLocal ? getLocalPrepPoint() : getOpponentPrepPoint();
            const px = prepPoint.x;
            const py = prepPoint.y;
            particleRef.spawnDoubleCrossCircle(px, py);
        }
        else if (eff.type === CardType.Energize) particleRef.spawnEnergizeEchoes();
        else if (eff.type === CardType.Shock) particleRef.spawnShockLightning(ox, oy, tx, ty, act.targetId.toString());
        else if (eff.type === CardType.PsySearch) particleRef.spawnPsySearchWeb();
        else if (eff.type === CardType.Trembling) particleRef.spawnTrembling(ox, oy, tx, ty);
        else if (eff.type === CardType.LifeDrain) particleRef.spawnLifeDrain(ox, oy, tx, ty);
        else if (eff.type === CardType.PsyBlock) {
            // Target the Opponent of the Card Owner
            const ownerId = latest.card.ownerId;
            const isOwnerLocal = ownerId === localPlayerId;

            // If I played it, target Opponent (aiAvatar). If Opponent played it, target Me (playerAvatar).
            const targetRect = isOwnerLocal ? getOpponentAvatarRect() : getLocalAvatarRect();

            if (targetRect) {
                const tpx = targetRect.left + targetRect.width / 2;
                const tpy = targetRect.top + targetRect.height / 2;
                particleRef.spawnPsyBlockHands(tpx, tpy);
            }
        }
        else if (eff.type === CardType.VoodooDoll) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            particleRef.spawnVoodooDoll(!isOwnerLocal);
        }
        else if (eff.type === CardType.DiscerningEye) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            particleRef.spawnDiscerningEye(!isOwnerLocal);
        }
        else if (eff.type === CardType.FortunesFavor) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const targetRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
            if (targetRect) {
                const tpx = targetRect.left + targetRect.width / 2;
                const tpy = targetRect.top + targetRect.height / 2;
                particleRef.spawnFortunesFavor(tpx, tpy);
            }
        }
        else if (eff.type === CardType.ArcaneBoots) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const targetRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
            if (targetRect) {
                const tpx = targetRect.left + targetRect.width / 2;
                const tpy = targetRect.top + targetRect.height / 2;
                particleRef.spawnArcaneBoots(tpx, tpy);
            }
        }
        else if (eff.type === CardType.Protector) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const finalRotation = (latest.rotation % 360 + 360) % 360;
            const isFlipped = finalRotation > 90 && finalRotation < 270;
            const isAttack = isFlipped;
            particleRef.spawnProtector(isAttack, !isOwnerLocal);
        }
        else if (eff.type === CardType.EnergySword) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            particleRef.spawnEnergySword(tx, ty, !isOwnerLocal);
        }
        else if (eff.type === CardType.Larcendagger) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            particleRef.spawnLarcendagger(ox, oy, tx, ty, !isOwnerLocal);
        }
        else if (eff.type === CardType.InfernoGauntlet) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            particleRef.spawnInfernoGauntlet(ox, oy, tx, ty, !isOwnerLocal);
        }
        else if (eff.type === CardType.FlyingScythe) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            particleRef.spawnFlyingScythe(ox, oy, tx, ty, !isOwnerLocal);
        }
        else if (eff.type === CardType.Invincible) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const targetRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
            if (targetRect) {
                const tpx = targetRect.left + targetRect.width / 2;
                const tpy = targetRect.top + targetRect.height / 2;
                particleRef.spawnInvincible(tpx, tpy);
            }
        }
        else if (eff.type === CardType.LifeCharger) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const targetRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
            if (targetRect) {
                const tpx = targetRect.left + targetRect.width / 2;
                const tpy = targetRect.top + targetRect.height / 2;
                particleRef.spawnLifeCharger(tpx, tpy);
            }
        }
        else if (eff.type === CardType.CursedIron) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const targetRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
            if (targetRect) {
                const tpx = targetRect.left + targetRect.width / 2;
                const tpy = targetRect.top + targetRect.height / 2;
                particleRef.spawnCursedIron(tpx, tpy, !isOwnerLocal);
            }
        }
        else if (eff.type === CardType.EyesOfMadness) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const targetRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
            if (targetRect) {
                const tpx = targetRect.left + targetRect.width / 2;
                const tpy = targetRect.top + targetRect.height / 2;
                particleRef.spawnEyesOfMadness(tpx, tpy);
            }
        }
        else if (eff.type === CardType.MadnessControl) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const targetRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
            if (targetRect) {
                const tpx = targetRect.left + targetRect.width / 2;
                const tpy = targetRect.top + targetRect.height / 2;
                particleRef.spawnMadnessControl(tpx, tpy);
            }
        }
        else if (eff.type === CardType.TwilightShift) {
            particleRef.spawnTwilightShiftEffect(centerX, centerY);
        }
        else if (eff.type === CardType.PlantedBomb) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const oppRect = isOwnerLocal ? getOpponentAvatarRect() : getLocalAvatarRect();

            const sourceX = ox;
            const sourceY = oy;
            const targetX = oppRect ? oppRect.left + oppRect.width / 2 : centerX;
            const targetY = oppRect ? oppRect.top + oppRect.height / 2 : centerY;

            particleRef.spawnPlantedBomb(sourceX, sourceY, targetX, targetY, !isOwnerLocal);
        }
        else if (eff.type === CardType.Chromabow) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const oppRect = isOwnerLocal ? getOpponentAvatarRect() : getLocalAvatarRect();

            const sourceX = ox;
            const sourceY = oy;
            const targetX = oppRect ? oppRect.left + oppRect.width / 2 : centerX;
            const targetY = oppRect ? oppRect.top + oppRect.height / 2 : centerY;

            particleRef.spawnChromabow(sourceX, sourceY, targetX, targetY, !isOwnerLocal);
        }
        else if (eff.type === CardType.NaturalGrip) {
            const isTargetLocal = act.targetId === localPlayerId;
            const targetRect = isTargetLocal ? getLocalAvatarRect() : getOpponentAvatarRect();

            if (targetRect) {
                const targetX = targetRect.left + targetRect.width / 2;
                const targetY = targetRect.top + targetRect.height / 2;
                particleRef.spawnNaturalGrip(targetX, targetY, !isTargetLocal);
            }
        }
        else if ((latest.card as any).faceSlug === 'thunder-sprint') {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const casterRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();

            if (casterRect) {
                const targetX = casterRect.left + casterRect.width / 2;
                const targetY = casterRect.top + casterRect.height / 2;
                particleRef.spawnThunderSpirit(targetX, targetY);
            }
        }
        else if ((latest.card as any).faceSlug === 'card-cyclone') {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const targetRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();

            if (targetRect) {
                const targetX = targetRect.left + targetRect.width / 2;
                const targetY = targetRect.top + targetRect.height / 2;
                particleRef.spawnCardCyclone(targetX, targetY, !isOwnerLocal);
            }
        }
        else if (eff.type === CardType.Counterblast) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const sourceRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
            const targetRect = isOwnerLocal ? getOpponentAvatarRect() : getLocalAvatarRect();

            if (sourceRect && targetRect) {
                const sourceX = sourceRect.left + sourceRect.width / 2;
                const sourceY = sourceRect.top + sourceRect.height / 2;
                const targetX = targetRect.left + targetRect.width / 2;
                const targetY = targetRect.top + targetRect.height / 2;
                particleRef.spawnCounterblast(sourceX, sourceY, targetX, targetY);
            }
        }

        else if (eff.type === CardType.BrokenSecret) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const oppRect = isOwnerLocal ? getOpponentAvatarRect() : getLocalAvatarRect();

            const sourceX = ox;
            const sourceY = Math.max(0, oy - 150);
            const targetX = oppRect ? oppRect.left + oppRect.width / 2 : centerX;
            const targetY = oppRect ? oppRect.top + oppRect.height / 2 : centerY;

            particleRef.spawnBrokenSecret(sourceX, sourceY, targetX, targetY);
        }
        else if (eff.type === CardType.SealOfSilence) {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const oppRect = isOwnerLocal ? getOpponentAvatarRect() : getLocalAvatarRect();

            const sourceX = ox;
            const sourceY = oy;
            const targetX = oppRect ? oppRect.left + oppRect.width / 2 : centerX;
            const targetY = oppRect ? oppRect.top + oppRect.height / 2 : centerY;

            particleRef.spawnSealOfSilence(sourceX, sourceY, targetX, targetY);
        }
        else if ((latest.card as any).faceSlug === 'spikes' || latest.card.id === 'Spikes') {
            const isOwnerLocal = latest.card.ownerId === localPlayerId;
            const ownerRect = isOwnerLocal ? getLocalAvatarRect() : getOpponentAvatarRect();
            const oppRect = isOwnerLocal ? getOpponentAvatarRect() : getLocalAvatarRect();

            const sourceX = ownerRect ? ownerRect.left + ownerRect.width / 2 : ox;
            const sourceY = ownerRect ? ownerRect.top + ownerRect.height / 2 : oy;
            const targetX = oppRect ? oppRect.left + oppRect.width / 2 : centerX;
            const targetY = oppRect ? oppRect.top + oppRect.height / 2 : centerY;

            particleRef.spawnSpikes(sourceX, sourceY, targetX, targetY);
        }
    }, [gameState?.activations, gameState?.players, localPlayerId, particleRef]);
};
